import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, Trash2, X, Save, Edit2 } from "lucide-react";

export interface BatchPreset {
  id: string;
  name: string;
  branches: string[];
  warehouses: string[];
}

const STORAGE_KEY = "batch_presets";

export const loadPresets = (): BatchPreset[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const savePresets = (presets: BatchPreset[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
};

const csvToList = (csv: string): string[] =>
  csv
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

interface BatchPresetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPresetsChanged: (presets: BatchPreset[]) => void;
}

export const BatchPresetModal: React.FC<BatchPresetModalProps> = ({
  isOpen,
  onClose,
  onPresetsChanged,
}) => {
  const [presets, setPresets] = useState<BatchPreset[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [branchesText, setBranchesText] = useState("");
  const [warehousesText, setWarehousesText] = useState("");

  useEffect(() => {
    if (isOpen) setPresets(loadPresets());
  }, [isOpen]);

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setBranchesText("");
    setWarehousesText("");
  };

  const persist = (next: BatchPreset[]) => {
    setPresets(next);
    savePresets(next);
    onPresetsChanged(next);
  };

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const branches = csvToList(branchesText);
    const warehouses = csvToList(warehousesText);

    if (editingId) {
      persist(
        presets.map((p) =>
          p.id === editingId ? { ...p, name: trimmed, branches, warehouses } : p,
        ),
      );
    } else {
      const newPreset: BatchPreset = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: trimmed,
        branches,
        warehouses,
      };
      persist([...presets, newPreset]);
    }
    resetForm();
  };

  const handleEdit = (preset: BatchPreset) => {
    setEditingId(preset.id);
    setName(preset.name);
    setBranchesText(preset.branches.join(", "));
    setWarehousesText(preset.warehouses.join(", "));
  };

  const handleDelete = (id: string) => {
    persist(presets.filter((p) => p.id !== id));
    if (editingId === id) resetForm();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">
            Batch Filter Presets
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          {/* Existing presets list */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              Saved Presets
            </h3>
            {presets.length === 0 ? (
              <div className="text-sm text-slate-400 italic">No presets yet.</div>
            ) : (
              <div className="space-y-2">
                {presets.map((p) => (
                  <div
                    key={p.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      editingId === p.id
                        ? "border-primary bg-primary/5"
                        : "border-slate-200 dark:border-slate-800"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm text-slate-900 dark:text-white truncate">
                        {p.name}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        Branches: {p.branches.join(", ") || "—"} | Warehouses:{" "}
                        {p.warehouses.join(", ") || "—"}
                      </div>
                    </div>
                    <button
                      onClick={() => handleEdit(p)}
                      className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
                      title="Edit"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(p.id)}
                      className="p-1.5 rounded hover:bg-rose-50 dark:hover:bg-rose-500/10 text-rose-500"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form */}
          <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              {editingId ? "Edit Preset" : "New Preset"}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. East Region"
                  className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                  Branches (comma-separated)
                </label>
                <input
                  type="text"
                  value={branchesText}
                  onChange={(e) => setBranchesText(e.target.value)}
                  placeholder="e.g. CLT, CHS, ..."
                  className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                  Plants (comma-separated)
                </label>
                <input
                  type="text"
                  value={warehousesText}
                  onChange={(e) => setWarehousesText(e.target.value)}
                  placeholder="e.g. 101, 202, ..."
                  className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={!name.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-semibold rounded-md"
                >
                  {editingId ? <Save size={14} /> : <Plus size={14} />}
                  {editingId ? "Update" : "Add Preset"}
                </button>
                {editingId && (
                  <button
                    onClick={resetForm}
                    className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold rounded-md"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
